import React, { useContext } from 'react';
import WaiverStatusBox from './WaiverStatusBox';
import WaiverViewField from './WaiverViewField';
import WaiverCategorySelector from './editor/WaiverCategorySelector';
import AirlineRelationships from './editor/AirlineRelationships';
import FlightSpecificCriteria from './editor/FlightSpecificCriteria';
import { WaiverContext } from './WaiverEditConfigView';

const WaiverViewer: React.FC = () => {
  const {
    waiver,
    allWaiverVersions,
    changeWaiverVersion,
    formatDate,
    shouldShowNotificationId,
    shouldShowComments,
    airportsByAirport,
    airportsSystemWide,
    airportsByCountry,
    isAllFlights,
    isDomesticFlightsOnly,
    isInternationalFlightsOnly,
    shouldShowAirlineRelationships,
    shouldShowSpecificFlights,
    updateSpecificFlights,
    hasWaiverDocument,
    documentLink,
    hasPolicyLinks,
  } = useContext(WaiverContext);

  return <div className="row waiver-editor">
    <div className="row waiver-title">
      <div className="col-md-12">
        <div className="col-md-6 title">Waiver Details</div>
        <div className="col-md-6">
          <div className="version-select form-group">
            <label htmlFor="waiverVersion">Version</label>
            <select
              className="dropdown-toggle version-dropdown form-control"
              id="waiverVersion"
              name="waiver-version"
              value={waiver.version}
              onChange={(e: any) => {
                changeWaiverVersion(+e.target.value);
              }}
            >
              {allWaiverVersions.map((waivey: any) => (
                <option key={waivey.version} value={waivey.version}>
                  {waivey.versionDescription}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>

    <div className="waiver-form form-inline">
      <div className="col-md-12 waiver-section">
        <div className="col-md-12 no-padding waiver-section-header">
          <div className="col-md-6">
            <div className="form-group">
              <label>Issuing Airline:</label>
              <div className="form-control waiver-data issuing-airline">
                {waiver.issuingAirlineCode}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <WaiverStatusBox waiver={waiver} readOnly={true} />
          </div>
        </div>

        <div className="col-md-12 no-padding">
          <div className="col-md-6">
            <WaiverViewField label="Issued/Updated" value={formatDate(waiver.publishedDate)} />
            <WaiverViewField label="Waiver Name" value={waiver.name} />
            <WaiverCategorySelector label="Waiver Category" value={waiver.category} readonly={true} />
            <WaiverViewField label="Description" value={waiver.description} large={true} />
            <WaiverViewField label="First Impacted Travel Date" value={formatDate(waiver.firstEffectiveDate)} />
            <WaiverViewField label="Last Impacted Travel Date" value={formatDate(waiver.lastEffectiveDate)} />
            <WaiverViewField label="Ticketed On/After" value={formatDate(waiver.ticketedOnAfter)} />
            <WaiverViewField label="Ticketed On/Before" value={formatDate(waiver.ticketedOnBefore)} />
            <WaiverViewField label="Reissue Ticket On/Before" value={formatDate(waiver.reIssuedTicketOnBefore)} />
            <WaiverViewField label="Ticket Stock(s)" value={waiver.ticketStocks} />
            <WaiverViewField label="First New Travel Date" value={formatDate(waiver.firstNewTravelDate)} />
            <WaiverViewField label="Last New Travel Date" value={formatDate(waiver.lastNewTravelDate)} />
            <WaiverViewField label="New Travel Dates Details" value={waiver.newTravelDates} large={true} />
            <WaiverViewField label="Refunds Applicable" value={waiver.refundsApplicable ? 'Yes' : 'No'} />
            <WaiverViewField label="Notification ID" value={waiver.changeEventId} condition={shouldShowNotificationId()} />
            <WaiverViewField label="Internal-only Comments" value={waiver.comments} large={true} condition={shouldShowComments()} />
          </div>

          <div className="col-md-6">
            <WaiverViewField label="Affected Airports" value={waiver.airportCodes} condition={airportsByAirport()} large={true} />
            <WaiverViewField label="Affected Airports" value="SYSTEM-WIDE" condition={airportsSystemWide()} />
            <WaiverViewField label="Affected Countries" value={waiver.countryCodes} condition={airportsByCountry()} />
            <WaiverViewField label="Domestic and International Flights" value={waiver.allFlights ? 'Yes' : 'No'} condition={isAllFlights()} />
            <WaiverViewField label="Domestic Flights Only" value={waiver.domesticFlightsOnly ? 'Yes' : 'No'} condition={isDomesticFlightsOnly()} />
            <WaiverViewField label="International Flights Only" value={waiver.internationalFlightsOnly ? 'Yes' : 'No'} condition={isInternationalFlightsOnly()} />

            {shouldShowAirlineRelationships() && (
              <div className="field-group">
                <AirlineRelationships label="Applicable Airline Relationships" relationships={waiver.airlineRelationships} readOnly={true} />
              </div>
            )}

            {shouldShowSpecificFlights() && (
              <div className="field-group">
                <FlightSpecificCriteria
                  label="Applicable Specific Flights"
                  addButtonLabel="Add Flight"
                  criteria={waiver.specificFlights}
                  onChange={updateSpecificFlights}
                  // name="flight-specific-criteria"
                  readOnly={true}
                />
              </div>
            )}

            <WaiverViewField label="Waiver Code" value={waiver.code} />
            <WaiverViewField label="Endorsement Box Policy" value={waiver.endorsementBoxPolicy} large={true} />
            <WaiverViewField label="Cabin to Cabin Code" value={waiver.codeForRebooking} />
            <WaiverViewField label="Waive Change Fee Code" value={waiver.codeForChangeFee} />
            <WaiverViewField label="OSI Requirements" value={waiver.osiRequirements} large={true} />
            <WaiverViewField label="Additional Info" value={waiver.additionalInfo} large={true} />

            {hasWaiverDocument() && (
              <div className="form-group mini-section">
                <label>Waiver Document:</label>
                <div className="waiver-item-list section-content">
                  <div className="form-control section-item waiver-item">
                    <a title="View Document" target="_blank" href={documentLink()}>
                      {waiver.documentName}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {hasPolicyLinks() && (
              <div className="form-group mini-section">
                <label>Policy URLs:</label>
                <div className="waiver-item-list section-content">
                  {waiver.policyLinks.map((policyLink: any, index: number) => (
                    <div key={index} className="form-control section-item waiver-item">
                      <a target="_blank" href={policyLink.url}>
                        {policyLink.name}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
};

export default WaiverViewer;